import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import classes from './NewUserDownloadAppPage.module.css';
import AuthPageWrapper from 'Components/Auth/AuthPageWrapper/AuthPageWrapper';
import Button from 'Components/UI_Library/FormElements/Button';
import AppStore from 'Components/Setup/User/NewUserDownloadAppPage/Images/app-store.png';
import GooglePlay from 'Components/Setup/User/NewUserDownloadAppPage/Images/google-play.png';
import MobileAppScreenshots from 'Components/Setup/User/NewUserDownloadAppPage/Images/mobile-app-screenshots.png';
import { useGetAttachedCompanies } from 'Backend/users/getAttachedCompanies';
import { useActivateUserInCompany } from 'Backend/user-management/activateUserInCompany';
import { setCurrentCompanyId } from 'Lib/Utils';
import { setAttachedCompanies } from 'Backend/users/utils/attachedCompanies';

const switchLink = '/switch';
const welcomeLink = '/welcome';

/** This component acts an interim step for a brand new invited user to be prompted into downloading the mobile app */
const NewUserDownloadAppPage = () => {
    const navigate = useNavigate();
    const [finalLink, setFinalLink] = useState(switchLink);
    const { data: pendingCompanyResults, isSuccess: pendingCompaniesSuccess } = useGetAttachedCompanies({
        status: 'PENDING',
    });
    const pendingCompanies = pendingCompanyResults?.data || [];
    const {
        data: activeCompanyResults,
        refetch: refetchActiveCompanies,
        isSuccess: activeCompaniesSuccess,
    } = useGetAttachedCompanies({
        status: 'ACTIVE',
    });
    const activeCompanies = activeCompanyResults?.data || [];

    // If there are no active companies and the user has one pending company, activate the user in that company and redirect to home page
    const activateSingleCompany =
        !activeCompanies?.length && pendingCompanies?.length === 1 && Boolean(pendingCompanies?.[0]?.companyId);

    // ADD/ACTIVATE YOURSELF IN COMPANY
    const {
        mutate: activateUserInCompany,
        isSuccess: successActivatingUserInCompany,
        isLoading: isLoadingActivatingUserInCompany,
    } = useActivateUserInCompany();

    // If the user does not have any companies, set the link to go to welcome page
    useEffect(() => {
        setFinalLink(!pendingCompanies?.length && !activeCompanies?.length ? welcomeLink : switchLink);
    }, [pendingCompaniesSuccess, activeCompaniesSuccess]);

    const addYourselfToCompany = async () => {
        const companyId = pendingCompanies?.[0]?.companyId;
        if (companyId) {
            activateUserInCompany({
                companyId,
            });
        }
    };

    useEffect(() => {
        // After successfully adding the user to company, update their attached companies stored in local storage then redirect to the app
        if (successActivatingUserInCompany) {
            updatePermissions();
        }
    }, [successActivatingUserInCompany]);

    const updatePermissions = async () => {
        const { data: attachedCompanies, isSuccess } = await refetchActiveCompanies();
        const currentPermissions = attachedCompanies?.data || [];
        setAttachedCompanies(currentPermissions);
        if (currentPermissions?.[0]?.companyId) {
            setCurrentCompanyId(currentPermissions[0].companyId);
        }
        if (isSuccess) {
            navigate('/');
        }
    };

    return (
        <AuthPageWrapper>
            <div className={classes.centered}>
                <div className={classes.heading}>
                    <h2>Download the mobile app</h2>
                    <p>
                        The Paytron mobile app is packed with useful features for business finance on the go. You can
                        use it to set up your corporate card and add it to your digital wallet, upload expenses, make
                        payments, approve items and more.
                    </p>
                </div>
                <img src={MobileAppScreenshots} alt="Phone Screenshots" className={classes.screenshots} />
                <div className={classes.buttons}>
                    <div className={classes.buttons}>
                        <a href="https://play.google.com/store/apps/details?id=au.com.paytron.app&pli=1">
                            <img src={GooglePlay} alt="Google Play" />
                        </a>
                        <a href="https://apps.apple.com/us/app/paytron/id1661719411">
                            <img src={AppStore} alt="App Store" />
                        </a>
                    </div>
                    {activateSingleCompany ? (
                        <Button
                            type="primary"
                            text="Go to the desktop app"
                            className={classes.appButton}
                            loading={isLoadingActivatingUserInCompany}
                            disabled={isLoadingActivatingUserInCompany}
                            onClick={addYourselfToCompany}
                        />
                    ) : (
                        <Button
                            type="primary"
                            text="Go to the desktop app"
                            className={classes.appButton}
                            href={finalLink}
                        />
                    )}
                </div>
            </div>
        </AuthPageWrapper>
    );
};

export default NewUserDownloadAppPage;
